import { parseTypeDescriptor as parseTypeDescriptorLocal } from '@/components/forms/ReferencePickerField';

// ============================================================================
// objectFormHelpers.ts — ЧИСТЫЕ вспомогательные функции формы объекта (SRP).
// Вынесено из AutoObjectForm (E2e13): нормализация дескрипторов типов пикера,
// статичные отборы + связи «Владелец» для формы выбора, парсинг типа подбора
// модуля. НЕ используют состояние/хуки — детерминированы (SSoT).
// ============================================================================

export interface PickAttrLike {
  name: string;
  types?: Array<{ typeName: string; fullName: string; objectType: string }> | string[];
  fkTable?: string;
  refTable?: string;
  selectionParams?: Array<{ field: string; condition: string; value?: string }>;
  selectionLinks?: Array<{ param: string; field: string }>;
}

export type SelectionFilter = { field: string; condition: string; value?: string };

export interface NormalizedTypeRef {
  typeName: string;
  fullName: string;
  objectType: string;
}

/** Нормализация типов пикера: дескрипторы-строки ('Справочник.X'/'entity.entities')
 *  и структуры {typeName, fullName, objectType} → единый массив структур.
 *  Ссылка без состава (fkTable/refTable) → тип по таблице (SSoT — detect). */
export function normalizePickTypes(attr: PickAttrLike): NormalizedTypeRef[] {
  const rawTypes = (attr.types || []) as any[];
  const normalized: NormalizedTypeRef[] = [];
  for (const t of rawTypes) {
    if (typeof t === 'string') {
      const parsed = parseTypeDescriptorLocal(t);
      if (parsed) normalized.push({ typeName: t, fullName: t, objectType: parsed.objectType });
    } else if (t && typeof t === 'object' && t.fullName) {
      normalized.push({ typeName: t.typeName || t.fullName, fullName: t.fullName, objectType: t.objectType || 'catalog' });
    }
  }
  if (normalized.length === 0 && (attr.fkTable || attr.refTable)) {
    const fk = attr.fkTable || attr.refTable || '';
    const objectType = fk.toLowerCase() === 'entities' ? 'entity' : 'catalog';
    normalized.push({ typeName: fk, fullName: fk, objectType });
  }
  return normalized;
}

/** Параметры выбора и связи параметров выбора (как в 1С) для пикера:
 *  статичные отборы + связь «Владелец» ← реквизит формы/колонка строки ТЧ
 *  (значение из контекста формы/строки ТЧ; пустое значение → пустой список). */
export function buildSelectionFilters(
  attr: PickAttrLike,
  context: Record<string, unknown>,
): SelectionFilter[] {
  const filters: SelectionFilter[] = [...(attr.selectionParams || [])];
  for (const link of attr.selectionLinks || []) {
    if (link.param !== 'Владелец' || !link.field) continue;
    const v = context[link.field];
    if (v === undefined || v === null || v === '') {
      filters.push({ field: 'owner_id', condition: '=', value: '__none__' });
      continue;
    }
    filters.push({ field: 'owner_id', condition: '=', value: String(v) });
  }
  return filters;
}

/** Данные для СОЗДАНИЯ через пикер (как в 1С): новая запись с предзаполненным
 *  владельцем из связи параметров выбора («Владелец» ← реквизит формы/строки ТЧ). */
export function buildCreateData(
  attr: PickAttrLike,
  context: Record<string, unknown>,
): Record<string, unknown> | undefined {
  let data: Record<string, unknown> | undefined;
  for (const link of attr.selectionLinks || []) {
    if (link.param !== 'Владелец' || !link.field) continue;
    const v = context[link.field];
    if (v === undefined || v === null || v === '') continue;
    data = { ...(data || {}), owner_id: String(v) };
  }
  return data;
}

/** Парсинг типа подбора модуля формы: 'catalog.Counterparties'/'справочник.Код' →
 *  {objectType, objectName}; неизвестный тип → null (вызывающий показывает ошибку). */
export function parseModulePickerType(type: string): { objectType: string; objectName: string } | null {
  const parts = type.split('.');
  if (parts.length < 2) return null;
  const rawType = parts[0].toLowerCase().trim();
  const objectName = parts.slice(1).join('.');
  const map: Record<string, string> = {
    catalog: 'catalog', catalogs: 'catalog', справочник: 'catalog', справочники: 'catalog',
    document: 'document', documents: 'document', документ: 'document', документы: 'document',
    enumeration: 'enumeration', enumerations: 'enumeration', перечисление: 'enumeration', перечисления: 'enumeration',
    register: 'register', registers: 'register', регистр: 'register', регистры: 'register',
    entity: 'entity', entities: 'entity', сущность: 'entity', сущности: 'entity',
  };
  const objectType = map[rawType];
  if (!objectType) return null;
  return { objectType, objectName };
}
